import Heading from '@/components/_atoms/Heading';
import { PrimaryDefaultButton } from '@/components/buttons/primary';
import Link from '@/components/Link';
import { COLORS, GRADIENTS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp, paddingX } from '@/styles/layout/utils';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';

type BannerStyle = 'light' | 'dark';

type CTA = {
  href: string;
  text: string;
  onClick?: () => void;
};

export interface CTABannerProps {
  heading: ReactNode;
  paragraph?: ReactNode;
  primaryCta: CTA;
  secondaryCta?: CTA;
  bannerStyle: BannerStyle;
}

const CTABanner: FC<CTABannerProps> = (props) => {
  return (
    <Banner bannerStyle={props.bannerStyle}>
      <LeftColumn bannerStyle={props.bannerStyle}>
        <Heading level={2}>{props.heading}</Heading>

        {props.paragraph ? <ContentWrapper>{props.paragraph}</ContentWrapper> : null}
      </LeftColumn>

      <RightColumn>
        <Link href={props.primaryCta.href}>
          <PrimaryButton bannerStyle={props.bannerStyle} onClick={props.primaryCta.onClick}>
            {props.primaryCta.text}
          </PrimaryButton>
        </Link>

        {props.secondaryCta ? (
          <Link href={props.secondaryCta.href}>
            <SecondaryButton bannerStyle={props.bannerStyle} onClick={props.secondaryCta.onClick}>
              {props.secondaryCta.text}
            </SecondaryButton>
          </Link>
        ) : null}
      </RightColumn>
    </Banner>
  );
};

export default CTABanner;

type WithBannerStyle = { bannerStyle: BannerStyle };

const Banner = styled.div<WithBannerStyle>`
  display: flex;
  padding: ${remCalc(30, 20)};
  flex-direction: column;
  align-items: center;
  gap: ${remCalc(30)};
  align-self: stretch;
  border-radius: 10px;
  text-align: center;

  ${(props) => {
    switch (props.bannerStyle) {
      case 'light':
        return css`
          background: ${COLORS.GRAY.TINT};
        `;
      case 'dark':
        return css`
          background: ${GRADIENTS.DARK};
        `;
    }
  }};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(40, 44)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingX(97)};
    gap: ${remCalc(20)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(60, 76)};
    flex-direction: row;
    align-items: center;
    text-align: left;
    justify-content: space-between;
    gap: ${remCalc(40)};
  }
`;

const LeftColumn = styled.div<WithBannerStyle>`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${(props) => {
    switch (props.bannerStyle) {
      case 'light':
        return css`
          color: ${COLORS.BLACK};
        `;
      case 'dark':
        return css`
          color: ${COLORS.WHITE};
        `;
    }
  }}

  ${mediaBreakpointUp('desktop')} {
    max-width: ${remCalc(660)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    max-width: ${remCalc(590)};
  }
`;

const ContentWrapper = styled.div`
  ${customTextSize(16, 24)};

  ${mediaBreakpointUp('desktop')} {
    max-width: ${remCalc(660)};
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
    align-items: center;
  }
`;

const PrimaryButton = styled(PrimaryDefaultButton)<WithBannerStyle>`
  ${(props) => {
    switch (props.bannerStyle) {
      case 'light':
        return css`
          --button-color: ${COLORS.RED.LOWLIGHT};
          --button-text-color: ${COLORS.WHITE};

          box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0)};

          &:hover {
            box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0.6)};
          }
        `;
      case 'dark':
        return css`
          --button-color: ${COLORS.WHITE};
          --button-text-color: ${COLORS.BLACK};
          --button-color-hover: ${COLORS.RED.LOWLIGHT};
          --button-text-color-hover: ${COLORS.WHITE};

          box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0)};

          &:hover {
            box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0.6)};
          }
        `;
    }
  }}
`;

const SecondaryButton = styled(PrimaryDefaultButton)<WithBannerStyle>`
  background-color: transparent;

  ${(props) => {
    switch (props.bannerStyle) {
      case 'light':
        return css`
          --button-color: ${COLORS.BLACK};
          --button-border-color: ${COLORS.BLACK};
          --button-text-color: ${COLORS.BLACK};
          --button-color-hover: ${COLORS.BLACK};
          --button-text-color-hover: ${COLORS.WHITE};
        `;
      case 'dark':
        return css`
          --button-color: ${COLORS.WHITE};
          --button-border-color: ${COLORS.WHITE};
          --button-text-color: ${COLORS.WHITE};
          --button-color-hover: ${COLORS.BLUE.LOWLIGHT};
          --button-text-color-hover: ${COLORS.WHITE};

          box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0)};

          &:hover {
            box-shadow: 0px 0px 20px ${hexToRGBA(COLORS.RED.LOWLIGHT, 0.6)};
          }
        `;
    }
  }}
`;
